import React, {
  useEffect,
  useMemo,
  useState,
  forwardRef,
  useImperativeHandle,
} from 'react';
import { LogType } from '../interfaces/LogType';
import './StatsWidget.css';
import {
  loadDoneStats,
  loadLocationStats,
  loadPilotStats,
  updateStats,
} from '../firebase/db-stats';
import { DroneStats, LocationStat, PilotStats } from '../interfaces/StatsType';
import { FilterType } from '../interfaces/FilterType';
import LoadingSpinner from './LoadingSpinner';
import { SettingsType } from '../interfaces/SettingsType';
import LocationsMap from './locationsmap';

interface StatistikWidgetProps {
  droneType?: string | number;
  pilotId?: string;
  isLocation?: boolean;
  isVisible?: boolean;
  verticalLayout?: boolean;
}

export interface StatsWidgetRef {
  refreshData: () => Promise<void>;
}

const StatistikWidget = forwardRef<StatsWidgetRef, StatistikWidgetProps>(
  (
    {
      droneType,
      pilotId,
      isLocation = false,
      isVisible = true,
      verticalLayout = false,
    },
    ref
  ) => {
    const [droneStats, setDroneStats] = useState<DroneStats | undefined>(
      undefined
    );
    const [pilotStats, setPilotStats] = useState<PilotStats | undefined>(
      undefined
    );
    const [locationSats, setLocationSats] = useState<
      LocationStat[] | undefined
    >(undefined);
    const [includes, setIncludes] = useState<FilterType[]>([]);
    const [loading, setLoading] = useState(false);
    const [viewMode, setViewMode] = useState<'map' | 'list'>('map');
    const [isExpanded, setIsExpanded] = useState(false);

    useEffect(() => {
      loadData();
    }, [droneType, pilotId]);

    async function loadData() {
      setLoading(true);
      if (droneType != undefined) {
        const tempData = await loadDoneStats(droneType);
        setDroneStats(tempData);
        setIncludes([{ field: 'droneType', operator: '==', value: droneType }]);
      }
      if (pilotId != undefined) {
        const tempData = await loadPilotStats(pilotId);
        setPilotStats(tempData);
        setIncludes([
          { field: 'pilotId', operator: '==', value: pilotId },
          { field: 'analystId', operator: '==', value: pilotId },
          { field: 'observerId', operator: '==', value: pilotId },
        ]);
      }
      if (isLocation) {
        const tempData = await loadLocationStats();
        setLocationSats(tempData);
        setIncludes([{ field: 'location', operator: '==', value: 'all' }]);
      }
      setLoading(false);
    }

    async function refreshData() {
      setLoading(true);
      await updateStats(includes);
      await loadData();
      setLoading(false);
    }

    useImperativeHandle(ref, () => ({
      refreshData,
    }));

    const widgetClassName = verticalLayout
      ? 'statistik-widget vertical'
      : 'statistik-widget';

    const itemClassName = verticalLayout
      ? 'statistik-item vertical'
      : 'statistik-item';

    const actionButton = verticalLayout
      ? 'action-button vertical'
      : 'action-button';

    if (droneType != undefined && droneStats != undefined) {
      return (
        <div className={widgetClassName}>
          {loading ? (
            <LoadingSpinner />
          ) : (
            <>
              <div className={itemClassName}>
                <h3>Gesamtflugzeit</h3>
                <p>
                  {Math.floor(droneStats.totalFlightTime / 60 / 60)}h{' '}
                  {((droneStats.totalFlightTime / 60) % 60).toFixed(0)}m
                </p>
              </div>
              <div className={itemClassName}>
                <h3>Anzahl Flüge</h3>
                <p>{droneStats.totalFlightCount}</p>
              </div>
              <div className={itemClassName}>
                <h3>Max. Höhe</h3>
                <p>{droneStats.maxFlightHeigth.toFixed(0)} m</p>
              </div>
              <div className={itemClassName}>
                <h3>Max. Km/h</h3>
                <p>{droneStats.maxFlightSpeed.toFixed(0)} km/h</p>
              </div>
            </>
          )}

          {verticalLayout && (
            <div className="button-group-statistic">
              <a href={'/' + droneType}>
                <button className={actionButton}>Flugbuch öffnen</button>
              </a>
            </div>
          )}
        </div>
      );
    }
    if (pilotId != undefined && pilotStats != undefined) {
      return (
        <>
          <div className="statistik-widget-dropdown">
            <div
              className="widget-header-dropdown"
              onClick={() => setIsExpanded(!isExpanded)}
              style={{ cursor: 'pointer' }}
            >
              <h3>Flugdaten</h3>
              <span className="material-icons expand-button">
                {isExpanded ? 'expand_less' : 'expand_more'}
              </span>
            </div>

            {isExpanded && (
              <div>
                <div className="pilot-dashboard">
                  <div className="dashboard-item">
                    <h3>Pilot</h3>
                    <p>
                      {Math.floor(pilotStats.totalPilotTime / 60 / 60)}h{' '}
                      {((pilotStats.totalPilotTime / 60) % 60).toFixed(0)}m
                    </p>
                  </div>
                  <div className="dashboard-item">
                    <h3>Bildauswerter</h3>
                    <p>
                      {Math.floor(pilotStats.totalAnalystTime / 60 / 60)}h{' '}
                      {((pilotStats.totalAnalystTime / 60) % 60).toFixed(0)}m
                    </p>
                  </div>
                  <div className="dashboard-item">
                    <h3>Luftraumbeobachter</h3>
                    <p>
                      {Math.floor(pilotStats.totalObserverTime / 60 / 60)}h{' '}
                      {((pilotStats.totalObserverTime / 60) % 60).toFixed(0)}m
                    </p>
                  </div>
                  <div className="dashboard-item">
                    <h3>Anzahl der Flüge (Pilot)</h3>
                    <p>{pilotStats.totalPilotCount}</p>
                  </div>
                  <div className="dashboard-item">
                    <h3>Anzahl der Flüge (Bildauswerter)</h3>
                    <p>{pilotStats.totalAnalystCount}</p>
                  </div>
                  <div className="dashboard-item">
                    <h3>Anzahl der Flüge (Luftraumbeobachter)</h3>
                    <p>{pilotStats.totalObserverCount}</p>
                  </div>
                </div>
                {loading ? (
                  <div className="refresh-container">
                    <LoadingSpinner />
                  </div>
                ) : (
                  <div className="refresh-container">
                    <span
                      onClick={() => refreshData()}
                      className="refresh-button"
                    >
                      <span className="material-symbols-outlined">refresh</span>
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>
        </>
      );
    }
    if (isLocation && locationSats) {
      return (
        <div className="map-widget">
          <div className="widget-header">
            <h2>Geflogene Orte:</h2>
            <button
              onClick={() => setViewMode(viewMode === 'map' ? 'list' : 'map')}
              className="toggle-view-button"
              title="Ansicht wechseln"
            >
              {viewMode === 'map' ? 'Liste' : 'Karte'}
            </button>
          </div>

          {viewMode === 'map' ? (
            <LocationsMap locations={locationSats} />
          ) : (
            <div className="statistik-widget">
              {locationSats.map((item, idx) => (
                <div key={idx} className="statistik-item">
                  <h3>{item.location}</h3>
                  <p>{item.totalFlights}</p>
                </div>
              ))}
            </div>
          )}
          {loading ? (
            <LoadingSpinner />
          ) : (
            <span onClick={() => refreshData()} className="refresh-button">
              <span className="material-symbols-outlined">refresh</span>
            </span>
          )}
        </div>
      );
    }
    return <></>;
  }
);

export default StatistikWidget;
